import * as THREE from 'three';
import { IPoint2d } from '../math/IPoint2d';
import { ESeparateWallType } from '../data/separateWall/SeparateWall';
import { EShortcutId } from '@/editor/controller/keyboardController/config';
import { IFurniture } from '../data/model/furniture';
import { IAssemblyData } from '../data/assembly/assembly';
import { IWindowData } from '../data/model/model';

export enum EMode {
  EMPTY = '',
  ADD = 'ADD',
  SELECT = 'SELECT'
}

export enum ESceneMode {
  scene2d = 'scene2d',
  scene3d = 'scene3d'
}

export enum EReplaceMaterialEntityType {
  FLOOR_MATERIAL = 'floor_material',
  WALL_MATERIAL = 'wall_material',
  GLASS_MATERIAL = 'glass_material'
}

export interface IOperateInfo {
  mode: EOperateMode;
  toolType: EToolType;
  /** 额外的状态 */
  extraState?: {
    /** 是否正在复制 */
    copying?: boolean;
    /** 阵列数据  */
    arrayData?: {
      entityId: string;
      entityType: EEntityType;
    };
    furnitureData?: {
      modelId: string;
    };
  };

  payload?: unknown;
}

/** 框选期间跟随光标的选择模式提示 */
export interface IBoxSelectHint {
  /** 光标在视口中的横坐标（clientX） */
  x: number;
  /** 光标在视口中的纵坐标（clientY） */
  y: number;
  /** 当前是否只框选家具/组合，false 表示隔墙也在框选范围内 */
  onlyFurniture: boolean;
}

export enum EOperateMode {
  EMPTY = 'empty',
  MultiSelect = 'multiSelect',
  DrawWall = 'drawWall',
  DrawRectWall = 'drawRectWall',
  DrawArcWall = 'drawArcWall',
  DrawDoorWindow = 'drawDoorWindow',
  DrawStruct = 'drawStruct',
  DrawFurniture = 'drawFurniture',
  DrawAssembly = 'drawAssembly',
  DrawMultiModel = 'drawMultiModel',
  DrawSeparateWall = 'drawSeparateWall',
  DrawArcSeparateWall = 'drawArcSeparateWall',
  Floorplan = 'floorplan',
  Layout = 'layout',
  Array = 'array',
  Alignment = 'alignment',
  MeasureRuler = 'measureRuler',
  DrawLinearDimension = 'drawLinearDimension',
  DrawAreaDimension = 'drawAreaDimension'
}

export enum EToolType {
  empty = '',
  lineWall = 'lineWall',
  rectWall = 'rectWall',
  arcWall = 'arcWall',
  squareColumn = 'squareColumn',
  cylinder = 'cylinder',
  bridge = 'bridge',
  stairs = 'stairs',
  singleDoor = 'singleDoor',
  doubleDoor = 'doubleDoor',
  motherDoor = 'motherDoor',
  window = 'window',
  frenchWindow = 'frenchWindow',
  model = 'model',
  virtualSeparateWall = 'virtualSeparateWall',
  separateWall = 'separateWall',
  AreaDimRect = 'areaDimRect',
  AreaDimFree = 'areaDimFree'
}

export interface ISceneInfo {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  intersectableGroup: THREE.Group;
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  canvasElement: HTMLCanvasElement;
}
export interface ISceneInfo2 {
  renderer: THREE.WebGLRenderer;
  canvasElement: HTMLCanvasElement;
  canvasContainer: HTMLDivElement;
  scene2d: {
    scene: THREE.Scene;
    intersectableGroup: THREE.Group;
    camera: THREE.OrthographicCamera;
  };
  scene3d: {
    scene: THREE.Scene;
    intersectableGroup: THREE.Group;
    camera: THREE.PerspectiveCamera & { userData: { target: THREE.Vector3 } };
  };
}

// 2d模型类型, 和entityType区分开
export enum EModel2dType {
  EMPTY = 'empty',
  // Base = 'base',
  VirturalArcWall = 'virturalArcWall'

  // FloorPlan = 'floorPlan',
  // Wall = 'wall',
  // Floor = 'floor',
  // Ceil = 'ceil',
  // DoorWindow = 'doorWindow',
  // Column = 'column',
  // Region = 'region',
  // OrthogonalLine = 'orthogonalLine',
  // VirtualWall = 'virtualWall',
  // HighlightedLine = 'highlightedLine',
  // WallExtremePoint = 'wallExtremePoint',
  // Transform = 'transform',
  // WallOutline = 'wallOutline',
  // // Model = 'model',
  // Furniture = 'furniture',
  // SeparateWall = 'separateWall',
  // Hightlight = 'Hightlight',
  // Face = 'face',
  // Room = 'room'
}

// entity类型
export enum EEntityType {
  EMPTY = '',
  FloorPlan = 'floorPlan',
  Base = 'base',
  Wall = 'wall',
  Floor = 'floor',
  Ceil = 'ceil',
  DoorWindow = 'doorWindow',
  Column = 'column',
  Region = 'region',
  OrthogonalLine = 'orthogonalLine',
  VirtualWall = 'virtualWall',
  HighlightedLine = 'highlightedLine',
  WallExtremePoint = 'wallExtremePoint',
  Transform = 'transform',
  RectangleHelper = 'rectangleHelper',
  TextInfo = 'textInfo',
  WallOutline = 'wallOutline',
  // Model = 'model',
  Furniture = 'furniture',
  SeparateWall = 'separateWall',
  Hightlight = 'Hightlight',
  Face = 'face',
  Room = 'room',
  ModelGroup = 'modelGroup',
  Assembly = 'assembly',
  MultiModel = 'multiModel',
  ArrayPreview = 'arrayPreview',
  /** 面积标注 */
  AreaDimension = 'areaDimension',
  AreaTransform = 'areaTransform',
  /** 尺寸标注 */
  LinearDimension = 'linearDimension'
}

export interface IEntityInfo {
  entityId: string;
  type: EEntityType;
}

export enum EContextAction {
  /** 全局布局，只支持房间 */
  LAYOUT_FULL = 'layoutFull',
  /** 局部布局， 只支持分区 */
  LAYOUT_PARTIAL = 'layoutPartial',
  /** 应用材质 */
  APPLY_MATERIAL = 'applyMaterial',
  /** 清空房间/分区 */
  CLEAR = 'clear',
  /** 移除 Entity */
  REMOVE = 'remove',
  /** 复制 Entity */
  COPY = 'copy',
  /** 翻转 */
  FLIP = 'flip',
  /** 替换 */
  REPLACE = 'replace',
  /** 锁定 */
  LOCK = 'lock',
  /** 隐藏 */
  HIDE = 'hide',
  /** 组合 */
  ASSEMBLE = 'assemble',
  /** 解组 */
  UNASSEMBLE = 'unassemble',
  /** 阵列 */
  ARRAY = 'array',
  /** 选择同款构件 */
  SELECT_SAME_MODEL = 'selectSameModel',
  /** 对齐 */
  ALIGN = 'align'
}

export interface IContextMenuItem {
  action: EContextAction;
  label: string;
  icon: string;
  shortcut?: EShortcutId;
  disabled?: boolean;
}

export interface EditParamsFormItem {
  id: string;
  label: string;
  value: string | number;
  type: 'text' | 'number' | 'select';
  unit?: string;
  options?: { label: string; value: string | number }[];
  settings?: Record<string, boolean>;
  onChange?: (value: unknown) => void;
}

export interface EditParamsPayload {
  id: string;
  type: EEntityType;
  form: {
    label: string;
    items: EditParamsFormItem[];
  }[];
}

/** 没有选中时: `{entityId: '', type: EEntityType.EMPTY}` */
export interface ISelected extends IEntityInfo {
  intersectionPoint?: IPoint2d;
  intersectionPoint3d?: THREE.Vector3;
}

// TODO: 待优化，使用ERelationOfPointOnSeg替代
export enum EPointOnSegmentType {
  StartPoint = 'startPoint',
  EndPoint = 'endPoint',
  Between = 'between',
  Empty = 'empty'
}

export enum EFaceSide {
  /** 其他 */
  Empty = 1,
  /** 左面 */
  MainLeftSide = 2,
  /** 右面 */
  MainRightSide = 3,
  /** 起点侧面/方柱背面 */
  StartSide = 4,
  /** 终点侧面/方柱前面 */
  EndSide = 5,
  /** 顶部侧面 */
  TopSide = 6,
  /** 底部侧面 */
  BottomSide = 7,
  /** 圆柱侧面 */
  CylinderSide = 8,
  /** 单面（baseVirtualWall） */
  Single = 9
}

export interface IPureEdge {
  start: IPoint2d;
  end: IPoint2d;
  center?: IPoint2d; // 弧 中心点
  clockwise?: boolean; // 弧 方向
}

export interface IWallEdge extends IPureEdge {
  wallId: string;
  side: EFaceSide;
  startId: number;
  endId: number;
  sepId?: string;
  sepSide?: EFaceSide;
  sepType?: ESeparateWallType;
  regionId?: string;
}

export interface IHistoryParams {
  layerHeight: number;
  offGround: number;
}

export const FLOOR_PLAN_CONFIG_VERSION = 2;
export const DXF_LEGACY_RAW_ASSET_FORMAT = 'dxf-raw';
export const DXF_BASEMAP_CONTEXT_ASSET_FORMAT = 'dxf-gzip';

export type TFloorPlanType = 'dxf' | 'pdf';
export type TDxfFloorPlanAssetFormat = typeof DXF_LEGACY_RAW_ASSET_FORMAT | typeof DXF_BASEMAP_CONTEXT_ASSET_FORMAT;

export interface IPdfFloorPlanConfig {
  id: string;
  type: 'pdf';
  name: string;
  url?: string;
  version: typeof FLOOR_PLAN_CONFIG_VERSION;
  config: {
    scale: number;
  };
  /**
   * @deprecated 仅用于读取 v1/旧服务端数据，保存时统一写入 config.scale。
   */
  scale?: number;
}

export interface IDxfFloorPlanConfig {
  id: string;
  type: 'dxf';
  name: string;
  url?: string;
  version: typeof FLOOR_PLAN_CONFIG_VERSION;
  config: {
    assetFormat: TDxfFloorPlanAssetFormat;
    contextVersion?: number;
    compression?: 'gzip';
    transformOcs: boolean;
    renderSolid?: boolean;
    dxfHiddenLayers?: string[];
  };
  /**
   * @deprecated 仅用于读取 v1/旧服务端数据，保存时统一写入 config。
   */
  scale?: number;
  /**
   * @deprecated 仅用于读取旧方案数据，保存时统一写入 config.dxfHiddenLayers。
   */
  dxfHiddenLayers?: string[];
}

export type IFloorPlanConfig = IPdfFloorPlanConfig | IDxfFloorPlanConfig;

export interface INotHistoryParams {
  floorPlanConfig: IFloorPlanConfig | null;
  wallThickness: number;
  separateWallThickness: number;
  separateWallType: ESeparateWallType;
}

export type ISystemParams = IHistoryParams & INotHistoryParams;

export interface IEdgeWallSideInfo {
  side: EFaceSide;
  wallId: string;
}

export interface IEdgeWallSideInfoWithRelative extends IEdgeWallSideInfo {
  startId: number;
  endId: number;
  sepId?: string;
  sepSide?: EFaceSide;
  sepType?: ESeparateWallType;
  regionId?: string;
}

export const WALL_FACE_SIDE_ARR = [
  EFaceSide.MainLeftSide,
  EFaceSide.MainRightSide,
  EFaceSide.StartSide,
  EFaceSide.EndSide,
  EFaceSide.TopSide,
  EFaceSide.BottomSide
];

export interface IFaceEdgeInfo {
  start: IPoint2d;
  end: IPoint2d;
  side: EFaceSide;
  wallId: string;
  startId: number;
  endId: number;

  height?: number;
  offGround?: number;
  baseSepWallId?: string;
}

export interface IEdgeInfo {
  start: IPoint2d;
  end: IPoint2d;
  side: EFaceSide;
  wallId: string;

  startId: number;
  endId: number;

  height?: number;
  offGround?: number;
  baseSepWallId?: string;
}

export interface IWallEdgeData {
  wallId: string;
  startEdges: IEdgeInfo[];
  endEdges: IEdgeInfo[];
  leftEdegs: IEdgeInfo[];
  rightEdegs: IEdgeInfo[];
}

export enum EPointType {
  Wall = 'w',
  Sep = 's'
}

// 墙端点类型
export enum EExtremePointType {
  StartPoint = 1,
  EndPoint = 2,
  ArcMidPoint = 3
}

export const EEdgeType = EPointType;

export enum EDirection {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right'
}

// 墙体联动操作类型
export enum EWallOperateType {
  StartPoint = 'startPoint', // 起点操作
  EndPoint = 'endPoint', // 终点操作
  ArcMidPoint = 'arcMidPoint', // 弧中点操作
  Pan = 'pan' // 平移操作
}

export interface IUniformWallData {
  id: string;
  start: IPoint2d;
  end: IPoint2d;
  thickness: number;
  center?: IPoint2d;
  clockwise?: boolean;
}

export interface IOriginModifyWallInfo {
  wallId: string;
  updateBaseData: Partial<IUniformWallData>;
}

export interface IBoundingBoxInfo {
  type: EEntityType;
  data: IFurniture | IAssemblyData | IWindowData;
}

export interface IBoxData {
  center: IPoint2d;
  width: number;
  thickness: number;
  rotation: number;
}

/** 阵列放置模式 */
export enum EArrayPlaceMode {
  Matrix = 'matrix',
  Line = 'line'
}

export interface IArrayGridData {
  end: IPoint2d;
  countX: number;
  countY: number;
  stepX: IPoint2d;
  stepY: IPoint2d;
  spacing: IPoint2d;
}

export enum EBoxSide {
  Top = 'top',
  Bottom = 'bottom',
  Left = 'left',
  Right = 'right'
}

export interface IBoxSize {
  width: number;
  thickness: number;
  height: number;
}

export interface IRotatedBoxPure extends IBoxSize {
  x: number;
  y: number;
  z: number;
  rotation: number;
}

export interface IRotatedBox {
  center: IPoint2d;
  width: number;
  thickness: number;
  rotation: number;
  preferAbsorbSide?: EBoxSide; // 优先吸附侧
}
